import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { fetchUserUnwinding } from "@/services";
import { QUERY_KEYS, QUERY_CONFIG } from "@/config";
import { type Key, useMemo } from "react";

export interface UnwindingPositionStore {
    id: Key | null | undefined;
    lockDuration: string;
    lockedBalance: string; // iUSD
    rewardsEarned: string; // iUSD
    startDate: string;
    endDate: string;
    epochsRemaining: number;
    startUnwindingTimestampMs: number;
    startUnwindingEpoch: number;
}

export interface UnwindingStore {
    unwindingPositions: UnwindingPositionStore[] | null;
    isLoading: boolean;
    isPending: boolean;
    error: Error | null;
    totalUnwinding: number;
    totalUnwindingRewards: number;
    totalRewards: number;
}

export const useUnwindingStore = (): UnwindingStore => {
    const { address } = useAccount();

    const {
        data: unwindingPositions,
        isLoading,
        isPending,
        error,
    } = useQuery<UnwindingPositionStore[]>({
        queryKey: QUERY_KEYS.UNWINDING.USER(address),
        queryFn: () => fetchUserUnwinding(address!),
        enabled: !!address,
        ...QUERY_CONFIG.UNWINDING,
    });

    const totalUnwinding = useMemo(
        () => unwindingPositions?.reduce((acc, pos) => acc + Number(pos.lockedBalance), 0) ?? 0,
        [unwindingPositions],
    );

    const totalUnwindingRewards = useMemo(
        () => unwindingPositions?.reduce((acc, pos) => acc + Number(pos.rewardsEarned), 0) ?? 0,
        [unwindingPositions],
    );

    return {
        error: error as Error | null,
        isLoading,
        isPending,
        totalUnwinding,
        totalUnwindingRewards,
        unwindingPositions: unwindingPositions ? unwindingPositions : [],
        totalRewards: unwindingPositions
            ? unwindingPositions.reduce((acc, pos) => acc + Number(pos.rewardsEarned), 0)
            : 0,
    };
};
